/**
 * ArrivalCinematic - the first-spawn reveal: a fade-from-black plus a short
 * (~2.6s) camera glide into the spawn pose, so a first-time visitor's very
 * first frames sweep INTO the hall instead of cutting to a static view.
 *
 * Robustness over spectacle:
 * - The glide is a manual smoothstep lerp on UpdateManager
 *   (no animation system dependencies) from a slightly pulled-back/elevated
 *   pose to the exact spawn pose the level already applied.
 * - Camera collisions/gravity are saved and disabled during the glide and
 *   restored to the known-good spawn pose afterwards. Input is suppressed by
 *   a full-screen shield (it also swallows the click that would otherwise
 *   engage pointer lock) plus per-frame neutralization of buffered movement -
 *   the camera's input attachment is deliberately left alone, because on
 *   touch devices TouchControls detaches individual built-in inputs and a
 *   blanket re-attach would double-drive the camera.
 * - ANY pointerdown or keydown skips straight to the final pose.
 * - Auto-skip (fade only) under navigator.webdriver (CI determinism - the
 *   same precedent as analytics) and prefers-reduced-motion.
 */
import { Vector3 } from '@babylonjs/core';
import { getGroundController } from '../../input/groundController.js';
import { UPDATE_ORDER, UpdateManager } from '../../utils/UpdateManager.js';

const DEFAULT_DURATION_MS = 2600;
const FADE_MS = 700;
const BACK_OFFSET = 3.5; // units behind the spawn pose along the view direction
const UP_OFFSET = 2.5; // units above it
const FOV_EASE = 0.1; // radians of extra FOV that eases out during the glide

/** @param {number} t */
function smoothstep(t) {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

/** @returns {HTMLDivElement} full-viewport black fade layer */
function makeFade() {
  const fade = document.createElement('div');
  fade.id = 'arrival-fade';
  fade.style.cssText =
    'position:fixed; inset:0; background:#000; z-index:901; pointer-events:none; opacity:1; transition:opacity 0.3s ease;';
  document.body.appendChild(fade);
  return fade;
}

/**
 * Returning-user reveal: just a quick fade from black, no camera motion.
 * @returns {Promise<void>}
 */
export function quickReveal() {
  const fade = makeFade();
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      fade.style.opacity = '0';
    });
    setTimeout(() => {
      fade.remove();
      resolve(undefined);
    }, 350);
  });
}

/**
 * Play the first-run arrival glide. Resolves when the camera is back at the
 * exact spawn pose with controls re-attached.
 * @param {import('@babylonjs/core').Scene} scene
 * @param {import('@babylonjs/core').UniversalCamera} camera
 * @param {{durationMs?: number}} [opts]
 * @returns {Promise<void>}
 */
export function playArrivalCinematic(scene, camera, opts = {}) {
  const reducedMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!scene || !camera || navigator.webdriver || reducedMotion) {
    return quickReveal();
  }

  const durationMs = opts.durationMs ?? DEFAULT_DURATION_MS;

  // Exact spawn pose (already applied by the level's load()).
  const targetPos = camera.position.clone();
  const targetFov = camera.fov;

  // Start slightly behind and above, along the spawn view direction. Small
  // offsets + the opening fade keep any transient wall clip invisible;
  // collisions are restored to the known-good spawn pose at the end.
  const forward = camera.getDirection(Vector3.Forward());
  const startPos = targetPos.subtract(forward.scale(BACK_OFFSET)).add(new Vector3(0, UP_OFFSET, 0));

  const savedCollisions = camera.checkCollisions;
  // The ground controller owns gravity; suspending it stops the glide's
  // scripted path being dragged to the floor mid-flight.
  const ground = getGroundController(scene);
  ground?.suspend('cinematic');
  camera.checkCollisions = false;

  // Shield: swallow the click that would engage pointer lock mid-glide; any
  // input skips to the end.
  const shield = document.createElement('div');
  shield.id = 'arrival-cinematic-shield';
  shield.style.cssText =
    'position:fixed; inset:0; z-index:900; background:transparent; pointer-events:auto; cursor:default;';
  document.body.appendChild(shield);

  const fade = makeFade();
  setTimeout(() => {
    fade.style.opacity = '0';
  }, 50);
  // The fade's own transition covers FADE_MS via CSS.
  fade.style.transition = `opacity ${FADE_MS}ms ease`;

  camera.position.copyFrom(startPos);
  camera.fov = targetFov + FOV_EASE;

  return new Promise((resolve) => {
    const startedAt = performance.now();
    let finished = false;
    const updateManager = UpdateManager.getInstance(scene);
    let updateId = null;

    const finish = () => {
      if (finished) return;
      finished = true;
      if (updateId != null) updateManager.unregister(updateId);
      camera.position.copyFrom(targetPos);
      camera.fov = targetFov;
      camera.checkCollisions = savedCollisions;
      // resume() re-grounds at the restored spawn pose (and never treats the
      // glide as horizontal travel).
      ground?.resume('cinematic');
      shield.remove();
      window.removeEventListener('keydown', onSkip, true);
      setTimeout(() => fade.remove(), FADE_MS);
      resolve(undefined);
    };

    const onSkip = () => finish();
    shield.addEventListener('pointerdown', onSkip);
    window.addEventListener('keydown', onSkip, true);

    updateId = updateManager.register(
      'arrivalCinematic',
      () => {
        const t = smoothstep((performance.now() - startedAt) / durationMs);
        Vector3.LerpToRef(startPos, targetPos, t, camera.position);
        camera.fov = targetFov + FOV_EASE * (1 - t);
        // Neutralize any buffered movement so nothing fights the glide.
        camera.cameraDirection.copyFromFloats(0, 0, 0);
        if (t >= 1) finish();
      },
      { priority: UPDATE_ORDER.POSE },
    );
  });
}
